import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import QualitySearchModal from './components/QualitySearchModal';
import { canUseChangeControl } from './changes/changeControl';

// ── Role helpers ──────────────────────────────────────────────────────────────

function normalizeRole(role) {
  if (!role) return '';
  if (Array.isArray(role)) return (role[0] || '').toLowerCase().trim();
  return String(role).toLowerCase().trim();
}
function canManage(role) {
  return ['admin', 'quality-manager', 'production-engineer', 'service'].includes(role);
}

// ── Section definitions ───────────────────────────────────────────────────────

const SECTIONS = {
  hub: { label: 'Product QMS', basePath: '/quality', color: 'var(--ni-green)' },
  pe: {
    label: 'Production Engineering', basePath: '/quality/pe-task-follow-up', color: '#fb923c',
    managerNav: [{ to: '/quality/pe-task-follow-up', label: 'Task Follow-Up', end: true }, { to: '/quality/pe-production-improvement', label: 'Production Improvement', end: true }],
    supplierNav: [{ to: '/quality/pe-task-follow-up', label: 'Task Follow-Up', end: true }, { to: '/quality/pe-production-improvement', label: 'Production Improvement', end: true }],
  },
  'change-control': {
    label: 'Change Control', basePath: '/quality/change-control', color: '#46BB89',
    managerNav: [{ to: '/quality/change-control', label: 'Changes', end: true }],
    supplierNav: [],
  },
  'sample-investigations': {
    label: 'Sample investigations', basePath: '/quality/sample-investigations', color: '#60a5fa',
    managerNav: [{ to: '/quality/sample-investigations', label: 'Investigation reports', end: true }],
    supplierNav: [],
  },
  'laser-engraving': {
    label: 'Laser Engraving', basePath: '/quality/laser-engraving', color: '#46BB89',
    managerNav: [{ to: '/quality/laser-engraving', label: 'Generators', end: true }],
    supplierNav: [{ to: '/quality/laser-engraving', label: 'Generators', end: true }],
  },
  documents: {
    label: 'Quality Documents', basePath: '/quality/documents', color: '#46BB89',
    managerNav: [
      { to: '/quality/documents',       label: 'Library',    end: true },
      { to: '/quality/documents/tree',  label: 'Tree' },
      { to: '/quality/templates',       label: 'Templates' },
      { to: '/quality/obsolete',        label: 'Obsolete' },
    ],
    supplierNav: [
      { to: '/quality/documents',       label: 'Library',    end: true },
      { to: '/quality/documents/tree',  label: 'Tree' },
    ],
    newRoute: '/quality/documents/new',
    newLabel: '+ New Document',
  },
  process: {
    label: 'Process Engineering', basePath: '/quality/process', color: '#60a5fa',
    managerNav: [
      { to: '/quality/process',         label: 'Flows',      end: true },
      { to: '/quality/notes',           label: 'Notes' },
    ],
    supplierNav: [
      { to: '/quality/process',         label: 'Flows',      end: true },
      { to: '/quality/notes',           label: 'My Notes' },
    ],
    newRoute: '/quality/process/new',
    newLabel: '+ New Flow',
  },
  suppliers: {
    label: 'Supplier Matrix', basePath: '/quality/suppliers', color: '#f59e0b',
    managerNav: [
      { to: '/quality/suppliers',       label: 'All Suppliers', end: true },
    ],
    supplierNav: [
      { to: '/quality/suppliers',       label: 'All Suppliers', end: true },
    ],
    newRoute: '/quality/suppliers/new',
    newLabel: '+ New Supplier',
  },
  jigs: {
    label: 'Jig Library', basePath: '/quality/jigs', color: '#a78bfa',
    managerNav: [
      { to: '/quality/jigs',            label: 'All Jigs',   end: true },
    ],
    supplierNav: [
      { to: '/quality/jigs',            label: 'All Jigs',   end: true },
    ],
    newRoute: '/quality/jigs/new',
    newLabel: '+ New Jig',
  },
  equipment: {
    label: 'Equipment Registry', basePath: '/quality/equipment', color: '#f59e0b',
    managerNav: [
      { to: '/quality/equipment',       label: 'All Equipment', end: true },
      { to: '/quality/calibration',     label: 'Calibration',   end: true },
    ],
    supplierNav: [
      { to: '/quality/equipment',       label: 'All Equipment', end: true },
      { to: '/quality/calibration',     label: 'Calibration',   end: true },
    ],
    newRoute: '/quality/equipment/new',
    newLabel: '+ New Equipment',
  },
  'test-library': {
    label: 'Test Library', basePath: '/quality/test-library', color: '#34d399',
    managerNav: [
      { to: '/quality/test-library',    label: 'All Tests',  end: true },
    ],
    supplierNav: [
      { to: '/quality/test-library',    label: 'All Tests',  end: true },
    ],
    newRoute: '/quality/test-library/new',
    newLabel: '+ New Test',
  },
  'test-tools': {
    label: 'Tools and software', basePath: '/quality/test-tools', color: '#34d399',
    managerNav: [{ to: '/quality/test-tools', label: 'Tool catalogue', end: true }],
    supplierNav: [{ to: '/quality/test-tools', label: 'Available tools', end: true }],
  },
  'assembly-instructions': {
    label: 'Assembly Instructions', basePath: '/quality/assembly-instructions', color: '#2dd4bf',
    managerNav: [
      { to: '/quality/assembly-instructions', label: 'All Instructions', end: true },
    ],
    supplierNav: [
      { to: '/quality/assembly-instructions', label: 'All Instructions', end: true },
    ],
    newRoute: '/quality/assembly-instructions/new',
    newLabel: '+ New Instruction',
  },
  boms: {
    label: 'Bills of Materials', basePath: '/quality/boms', color: '#f472b6',
    managerNav: [
      { to: '/quality/boms', label: 'All BOMs', end: true },
    ],
    // BOMs are NI-controlled; suppliers may view but the page gates writes.
    supplierNav: [
      { to: '/quality/boms', label: 'All BOMs', end: true },
    ],
    newRoute: '/quality/boms/new',
    newLabel: '+ New BOM',
  },
  standards: {
    label: 'Compliance Standards', basePath: '/quality/standards', color: '#818cf8',
    managerNav: [
      { to: '/quality/standards', label: 'Catalogue', end: true },
    ],
    // Suppliers may view the catalogue (to see what they must comply with) but
    // not edit it — the page itself gates add/edit/seed to NI staff.
    supplierNav: [
      { to: '/quality/standards', label: 'Catalogue', end: true },
    ],
  },
  notes: {
    label: 'Resource Notes', basePath: '/quality/notes', color: '#fb923c',
    managerNav: [
      { to: '/quality/notes',           label: 'Board',     end: true },
    ],
    supplierNav: [
      { to: '/quality/notes',           label: 'My Notes',   end: true },
    ],
  },
  analytics: {
    label: 'QMS Analytics', basePath: '/quality/analytics', color: '#38bdf8',
    managerNav: [
      { to: '/quality/analytics', label: 'Dashboard', end: true },
    ],
    supplierNav: [],
  },
  execution: {
    label: 'Quality Execution', basePath: '/quality/execution', color: '#e879f9',
    managerNav: [
      { to: '/quality/execution',       label: 'Work Orders', end: true },
    ],
    // Quality Execution is internal NI production sign-off — not exposed to
    // suppliers (also absent from the supplier hub; the API blocks them too).
    supplierNav: [],
    newRoute: '/quality/execution/new',
    newLabel: '+ New Work Order',
  },
};

function detectSection(pathname) {
  if (pathname === '/quality' || pathname === '/quality/') return 'hub';
  if (pathname.startsWith('/quality/pe-')) return 'pe';
  if (pathname.startsWith('/quality/change-control')) return 'change-control';
  if (pathname.startsWith('/quality/sample-investigations')) return 'sample-investigations';
  if (pathname.startsWith('/quality/laser-engraving')) return 'laser-engraving';
  if (pathname.startsWith('/quality/process'))      return 'process';
  if (pathname.startsWith('/quality/suppliers'))    return 'suppliers';
  if (pathname.startsWith('/quality/jigs'))         return 'jigs';
  if (pathname.startsWith('/quality/equipment'))    return 'equipment';
  if (pathname.startsWith('/quality/calibration'))  return 'equipment';
  if (pathname.startsWith('/quality/test-library')) return 'test-library';
  if (pathname.startsWith('/quality/test-tools') || pathname.startsWith('/quality/test-runtime') || pathname.startsWith('/quality/northstar')) return 'test-tools';
  if (pathname.startsWith('/quality/assembly-instructions')) return 'assembly-instructions';
  if (pathname.startsWith('/quality/boms'))         return 'boms';
  if (pathname.startsWith('/quality/standards'))    return 'standards';
  if (pathname.startsWith('/quality/notes'))        return 'notes';
  if (pathname.startsWith('/quality/analytics'))    return 'analytics';
  if (pathname.startsWith('/quality/execution'))    return 'execution';
  // documents, templates, obsolete, tree
  return 'documents';
}

// ── Layout ────────────────────────────────────────────────────────────────────

export default function QualityLayout() {
  const { user } = useUser();
  const navigate  = useNavigate();
  const { pathname } = useLocation();

  const role     = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const isEditor = canManage(role);
  const supplierKey = ['gpv','ubiqconn','acmepoint','hk_ringer'].includes(String(role||'').replace(/^test-/,''));
  const SUPPLIER_CREATE = new Set(['jigs', 'equipment', 'test-library', 'assembly-instructions', 'process', 'documents']);

  const [showSearch, setShowSearch] = useState(false);

  const sectionKey = detectSection(pathname);
  const section    = SECTIONS[sectionKey];
  const isHub      = sectionKey === 'hub';

  const navItems = section && !isHub
    ? ((['change-control', 'sample-investigations'].includes(sectionKey) ? canUseChangeControl(user) : isEditor) ? section.managerNav : section.supplierNav) || []
    : [];

  const sectionColor = section?.color || 'var(--ni-green)';

  return (
    <div style={{ minHeight: '100vh', background: 'var(--ni-ink)', color: 'var(--ni-text)' }}>
      {/* Top bar */}
      <div style={{
        background: 'var(--ni-char)',
        borderBottom: '1px solid var(--ni-border-soft)',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        height: 52,
      }}>
        {/* Back to hub (when inside a section) */}
        {!isHub && (
          <button
            onClick={() => navigate('/quality')}
            style={{ background: 'none', border: 'none', color: 'var(--ni-text-muted)', cursor: 'pointer',
              fontSize: 13, padding: '0 16px 0 0', marginRight: 8,
              borderRight: '1px solid var(--ni-border-soft)', height: '100%' }}
          >
            ← QMS
          </button>
        )}

        {/* Back to main (always) */}
        {isHub && (
          <button
            onClick={() => navigate('/')}
            style={{ background: 'none', border: 'none', color: 'var(--ni-text-muted)', cursor: 'pointer',
              fontSize: 13, padding: '0 16px 0 0', marginRight: 8,
              borderRight: '1px solid var(--ni-border-soft)', height: '100%' }}
          >
            ← Main
          </button>
        )}

        {/* Section label */}
        <span style={{ color: sectionColor, fontWeight: 700, fontSize: 14,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          marginRight: navItems.length ? 24 : 0 }}>
          {isHub ? 'Product QMS' : section?.label}
        </span>

        {/* Section nav */}
        {navItems.length > 0 && (
          <nav style={{ display: 'flex', gap: 4, flex: 1 }}>
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                style={({ isActive }) => ({
                  padding: '6px 14px',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  textDecoration: 'none',
                  letterSpacing: '0.04em',
                  color: isActive ? sectionColor : 'var(--ni-text-muted)',
                  background: isActive ? sectionColor + '18' : 'transparent',
                  transition: 'all 0.15s',
                })}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        )}

        {/* Search */}
        <button onClick={() => setShowSearch(true)}
          style={{ marginLeft:'auto', background:'rgba(255,255,255,0.06)',
            border:'1px solid var(--ni-border-soft)', color:'var(--ni-text-muted)',
            borderRadius:6, padding:'5px 14px', fontSize:12, cursor:'pointer',
            display:'flex', alignItems:'center', gap:6 }}>
          🔍 <span>Search</span>
        </button>

        {/* + New button (section editors only) */}
        {!isHub && (isEditor || (supplierKey && SUPPLIER_CREATE.has(sectionKey))) && section?.newRoute && (
          <button
            onClick={() => navigate(section.newRoute)}
            style={{
              marginLeft: 'auto',
              background: sectionColor,
              color: '#000',
              border: 'none',
              borderRadius: 6,
              padding: '6px 16px',
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
              letterSpacing: '0.04em',
              whiteSpace: 'nowrap',
            }}
          >
            {section.newLabel}
          </button>
        )}
      </div>

      {/* Page content */}
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '32px 24px' }}>
        <Outlet />
      </div>

      {showSearch && <QualitySearchModal onClose={() => setShowSearch(false)} />}
    </div>
  );
}
